<template>
    <div class="survey-note-form">

        <!-- Chief complaint -->

        <b-card no-body class="light-bg">
            <b-card-body class="pb-0">
                <b-form-group
                    :label="$i18n.gt('LABEL_REASON_VISIT_CHIEF_COMPLAINT')"
                    label-class="font-weight-bold">
                    <b-row>
                        <div class="col-md-6">
                            <b-form-textarea v-model="draftSurveyNote.chief_complaint"
                                             :placeholder="$i18n.gt('LABEL_ENTER_TEXT')"
                                             debounce="1000">
                            </b-form-textarea>
                        </div>
                    </b-row>
                </b-form-group>
            </b-card-body>
        </b-card>

        <!-- Subjective -->

        <b-card no-body class="soap-section light-bg">
            <b-badge class="soap-badge">S</b-badge>
            <div class="d-flex flex-column flex-md-row">
                <div class="soap-section__body col-md-6">
                    <survey-note-form-generated-text :score-names="textGroups.lifestyle"
                                                     v-if="showGeneratedText">
                    </survey-note-form-generated-text>

                    <b-form-textarea v-model="entered.s"
                                     :placeholder="$i18n.gt('LABEL_ADDITIONAL_COMMENTS')"
                                     debounce="1000">
                    </b-form-textarea>
                </div>

                <div class="soap-section-supporting-data col-md-6"
                     v-if="showSTrendCharts">
                    <div v-if="noteType == 'COACHING'">
                        <survey-note-form-coaching-summary :areas="['HABITS', 'LOGS', 'MINDSET']"/>
                    </div>
                    <div v-else>
                        <survey-note-form-trend-charts :score-groups="scoreGroups.s">
                        </survey-note-form-trend-charts>
                    </div>
                </div>
            </div>
        </b-card>

        <!-- Objective -->

        <b-card no-body class="soap-section light-bg">
            <b-badge class="soap-badge">O</b-badge>
            <div class="d-flex flex-column flex-md-row">
                <div class="soap-section__body col-md-6">
                    <b-row v-if="noteType == 'RPM_REVIEW'">
                        <b-col class="survey-note-form__note-text">
                            {{ $store.state.draftSurveyNote.text.generated.o }}
                        </b-col>
                    </b-row>

                    <b-row v-else
                           :cols="$mq == 'sm' ? 1 : 2">
                        <b-col>
                            <survey-note-form-generated-text :score-names="textGroups.pf"
                                                             v-if="showGeneratedText">
                            </survey-note-form-generated-text>
                        </b-col>
                        <b-col>
                            <survey-note-form-generated-text :score-names="textGroups.biometrics"
                                                             v-if="showGeneratedText">
                            </survey-note-form-generated-text>
                        </b-col>
                    </b-row>

                    <b-form-textarea v-model="entered.o"
                                     :placeholder="$i18n.gt('LABEL_OTHER_OBSERVATIONS')"
                                     debounce="1000">
                    </b-form-textarea>
                </div>
                <div class="soap-section-supporting-data col-md-6">
                    <div v-if="noteType == 'RPM_REVIEW'">
                        <survey-note-form-coaching-summary :areas="['LOGS']">
                        </survey-note-form-coaching-summary>
                    </div>
                    <div v-else>
                        <survey-note-form-trend-charts :score-groups="scoreGroups.o">
                        </survey-note-form-trend-charts>
                    </div>
                </div>
            </div>
        </b-card>

        <!-- Assessment -->

        <b-card no-body class="soap-section light-bg">
            <b-badge class="soap-badge">A</b-badge>

            <div class="d-flex flex-column flex-md-row">

                <div class="soap-section__body col-md-6">
                    <survey-note-form-generated-text :score-names="textGroups.overall"
                                                     v-if="showGeneratedText">
                    </survey-note-form-generated-text>

                    <b-form-textarea class="mb-3"
                                     v-model="entered.a"
                                     :placeholder="$i18n.gt('LABEL_OVERALL_ASSESSMENT')"
                                     debounce="1000">
                    </b-form-textarea>
                </div>
                <div class="soap-section-supporting-data col-md-6"
                     v-if="showATrendCharts || noteType == 'COACHING'">
                    <div v-if="noteType == 'COACHING'">
                        <survey-note-form-coaching-summary
                            :areas="['GOALS', 'PATH']"></survey-note-form-coaching-summary>
                    </div>
                    <div v-else>
                        <survey-note-form-trend-charts :score-groups="scoreGroups.a">
                        </survey-note-form-trend-charts>
                    </div>
                </div>
            </div>
        </b-card>

        <!-- Plan of Care -->

        <b-card no-body class="soap-section light-bg">
            <b-badge class="soap-badge">P</b-badge>
            <div class="d-flex flex-column flex-md-row">

                <div class="soap-section__body col-md-6">
                    <b-form-textarea class="my-3"
                                     v-model="entered.p"
                                     :placeholder="$i18n.gt('LABEL_PLAN_OF_CARE')"
                                     debounce="1000">
                    </b-form-textarea>

                    <!-- CCM -->

                    <b-card v-if="noteType == 'CCM'">
                        <survey-note-form-ccm>
                        </survey-note-form-ccm>
                    </b-card>

                    <!-- AWV -->

                    <b-card v-if="noteType == 'AWV'">
                        <survey-note-form-awv>
                        </survey-note-form-awv>
                    </b-card>
                </div>
                <div class="soap-section-supporting-data col-md-6">
                    <h4>{{ $i18n.gt('HEADING_RESOURCES') }}</h4>
                    <survey-note-form-resources score-name="SCORE_EXER">
                    </survey-note-form-resources>

                    <h4>{{ $i18n.gt('HEADING_HANDOUTS') }}</h4>
                    <survey-note-form-handouts>
                    </survey-note-form-handouts>
                </div>
            </div>
        </b-card>

    </div>
</template>

<script>
import SurveyNoteFormCoachingSummary from "@/components/SurveyNoteFormCoachingSummary";

/**
 * Form for editing a SurveyNote
 */
export default {
    name: 'SurveyNoteForm',
    components: {SurveyNoteFormCoachingSummary},
    data() {
        return {
            coachingAreas: {
                s: ['Habits', 'Logs'],
                a: ['Goals', 'Path'],
            },
            textGroups: {
                lifestyle: ['SCORE_EXER', 'SCORE_SED', 'SCORE_DIET', 'SCORE_STRESS', 'SCORE_SLEEP'],
                biometrics: ['BIO_BMI', 'SCORE_BP', 'BIO_LDL', 'BIO_HDL'],
                pf: ['SCORE_GAIT_SPEED', 'SCORE_SIT_TO_STAND', 'SCORE_STAIRS', 'SCORE_SIT_REACH', 'SCORE_CURLS'],
                overall: ['FRAMINGHAM_SCORE', 'ACC_AHA_SCORE', 'TOTAL_SCORE'],
            },
            scoreGroups: {
                s: [{
                    title: this.$i18n.gt('HEADING_REPORT_LIFESTYLE_RISK_FACTORS'),
                    cols: 2,
                    trendCharts: ['SCORE_EXER', 'SCORE_DIET', 'SCORE_STRESS', 'SCORE_SLEEP'],
                }],
                o: [{
                    title: this.$i18n.gt('HEADING_REPORT_SOCIAL_FACTORS'),
                    cols: 2,
                    isIndicatorLights: true,
                    trendCharts: [
                        'OK_FEELS_WELL', 'OK_HRQOL', 'OK_LITERACY', 'OK_SECURITY', 'OK_SOCIAL_SUPPORT',
                        'OK_HEALTH_LITERACY', 'OK_FINANCES', 'OK_FOOD_SECURITY', 'OK_COMMUNITY',
                        'OK_ADLS', 'OK_POVERTY', 'OK_TRANSPORTATION', 'OK_CENTERED'
                    ]
                }, {
                    title: this.$i18n.gt('HEADING_REPORT_PHYSICAL_FUNCTIONING'),
                    cols: 2,
                    trendCharts: ['SCORE_GAIT_SPEED', 'SCORE_SIT_TO_STAND', 'SCORE_STAIRS', 'SCORE_SIT_REACH', 'SCORE_CURLS']
                }, {
                    title: this.$i18n.gt('HEADING_REPORT_MENTAL_HEALTH_SCREENING'),
                    cols: 2,
                    trendCharts: ['PHQ9_SCORE', 'GAD7_SCORE', 'AUDIT_SCORE', 'ORT_SCORE']
                }, {
                    title: this.$i18n.gt('HEADING_REPORT_BIOMETRICS'),
                    cols: 2,
                    trendCharts: ['BIO_BMI', 'SCORE_BP', 'BIO_LDL', 'SCORE_METAB']
                }],
                a: [{
                    title: this.$i18n.gt('HEADING_REPORT_OVERALL_RISK'),
                    cols: 2,
                    trendCharts: ['FRAMINGHAM_SCORE', 'ACC_AHA_SCORE', 'TOTAL_SCORE']
                }],

            }
        }
    },
    computed: {
        draftSurveyNote() {
            return this.$store.state.draftSurveyNote
        },
        noteType() {
            return this.draftSurveyNote.note_type
        },
        entered() {
            return this.draftSurveyNote.text ? this.draftSurveyNote.text.entered : {}
        },
        showGeneratedText() {
            return this.noteType == 'AWV'
        },
        showSTrendCharts() {
            return this.noteType != 'RPM_REVIEW'
        },
        showATrendCharts() {
            return this.noteType == 'AWV'
        },
        showResourcesAndHandouts() {
            return this.noteType != 'RPM_REVIEW'
        }
    },
}
</script>

<style lang="scss">

//@import "variables_mixins";

.survey-note-form {

    .btn-group-toggle {
        label {
            display: flex;
            align-items: center;
        }
    }
}

.survey-note-form__note-text {
    white-space: pre-line;
    font-weight: normal;
    padding-bottom: 0;

    li {
        margin-left: 30px;
    }
}

.soap-section {
    margin-bottom: 30px;

    .soap-badge {
        position: absolute;
        left: -1px;
        top: -1px;
        width: 50px;
        font-size: 1.2em;
        color: #000;
        background-color: #ddd;

        border-top-right-radius: 0;
        border-bottom-left-radius: 0;
        border-top-left-radius: 4px;
        border-bottom-right-radius: 4px;
        padding: .5em .7em;
    }

    .soap-section-supporting-data {
        padding: 20px;

        .card-header {
            background-color: transparent;
        }

        .btn-group, .btn-group-vertical {
            margin-bottom: 20px;

            label {
                &.active {
                    background-color: var(--btn-bg-hover) !important;
                    border-color: var(--btn-bg-hover) !important;
                    color: #fff;

                    span {
                        color: #fff;
                    }
                }
            }
        }

        .btn-group {
            label {
                &:first-child {
                    border-top-left-radius: 3px;
                    border-bottom-left-radius: 3px;
                }

                &:last-child {
                    border-top-right-radius: 3px;
                    border-bottom-right-radius: 3px;
                }
            }
        }

        .btn-group-vertical {
            label {
                &:first-child {
                    border-top-left-radius: 3px;
                    border-top-right-radius: 3px;
                }

                &:last-child {
                    border-bottom-left-radius: 3px;
                    border-bottom-right-radius: 3px;
                }
            }
        }

    }
}

.soap-section__body {
    padding-top: 50px;
    padding-bottom: 15px;
    border-right: solid 1px var(--border-color);

    textarea {
        min-height: 80px;
    }
}

.survey-note-form-trend-charts {
    .report-indicator-light-mini {

    }

    .report-indicator-light-mini--light {
        transition: all .3s;

        &:hover {
            background-color: #e5e5e5;
        }

        &.report-indicator-light-mini--lit-up:hover {
            color: #5E4D00;
            background-color: #FFD84D;
            border-color: #FFD001;
        }
    }
}

</style>
